import { createAsyncThunk } from "@reduxjs/toolkit";
import { logout } from "../reducers/authReducer";
import {
  setCallbackServerIp,
  setIpList,
  setSelectedSenderNumber,
  setSenderNumbers,
  setCallbackUrl,
  setClientId,
  resetSettings,
  setCallbackApiKey,
} from "../reducers/settingsReducer";
import { transformClientDataFromOffice } from "../../transformers/office/ingressTransformer";
import { setCompanyInfo, setAccountType, resetBilling } from "../reducers/billingReducer";
import { showLoader, hideLoader } from "../reducers/loaderReducer";
import { api } from "../../utils/api";
import { FORTHCODE_SERVER_IP } from "../../utils/constants";

const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 60 * 1000; // 2h
const LOCAL_STORAGE_KEYS = {
  TOKEN_EXP: "tokenExp",
  CLIENT_ID: "clientId",
  TOKEN: "token",
  NAME: "name",
  EMAIL: "email",
  LANG: "lang",
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(
        "/clients/login",
        JSON.stringify(credentials)
      );
      setUserData({
        user: response.data.user,
        token: response.data.token,
        exp: response.data.exp,
        dispatch: dispatch,
      });
    } catch (error) {
      let msgToUser = "Unable to log in";
      if (error?.status === 401)
        msgToUser = "The email or password provided is incorrect.";
      return rejectWithValue(msgToUser);
    }
  }
);

export const verifyMe = createAsyncThunk(
  "auth/verifyMe",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const cs = getClientSecrets();
      if (!cs || !cs.token || !cs.exp)
        throw Error(
          "Unable to retrieve token or its expiriy from local storage"
        );
      let response;
      if (cs.exp) {
        // Convert to milliseconds for JavaScript Date object
        const timestampMilliseconds = cs.exp * 1000;
        // Create a Date object
        const dateFromTimestamp = new Date(timestampMilliseconds);
        // Get the current UTC time
        const currentUtcTime = new Date();
        if (
          dateFromTimestamp > currentUtcTime &&
          dateFromTimestamp - currentUtcTime <= TOKEN_REFRESH_BUFFER_MS
        ) {
          response = await api.post("/clients/refresh-token");
        } else {
          response = await api.get("/clients/me");
        }
        if (!response.data.user) throw Error("Unable to verify user");
        setUserData({
          user: response.data.user,
          token: response.data.token || response.data.refreshedToken,
          exp: response.data.exp,
          dispatch: dispatch,
        });
      }
    } catch (error) {
      dispatch(logoutUser());
      return false;
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const getClientSecrets = () => {
  try {
    // const apiKey = localStorage.getItem("apiKey");
    const clientId = localStorage.getItem(LOCAL_STORAGE_KEYS.CLIENT_ID);
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
    const exp = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXP);
    return {
      // apiKey,
      token,
      clientId,
      exp,
    };
  } catch (error) {
    console.error("getClientSecrets | error: ", error);
    return { token: false, clientId: false };
  }
};

export const logoutUser = () => (dispatch) => {
  removeUserDataFromLocalStorage();
  dispatch(logout());
  dispatch(resetBilling())
  dispatch(resetSettings())
};

const removeUserDataFromLocalStorage = () => {
  // Remove token and user data from localStorage
  // localStorage.removeItem("apiKey");
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.NAME);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.EMAIL);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.CLIENT_ID);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXP);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.LANG);

  // Optionally, remove the token from axios headers
  delete api.defaults.headers.common["Authorization"];
};

/**
 * Sets user related data as client into Redux or as user auth valuse into localhost.
 * @param {*} param0 
 */
const setUserData = ({ user, token, exp, dispatch }) => {
  try {
    const { name, email, id } = user;

    // Save token to localStorage
    // localStorage.setItem("apiKey", apiKey);
    localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.CLIENT_ID, id);
    localStorage.setItem(LOCAL_STORAGE_KEYS.NAME, name);
    localStorage.setItem(LOCAL_STORAGE_KEYS.EMAIL, email);
    localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_EXP, exp);
    FORTHCODE_SERVER_IP
      ? dispatch(setCallbackServerIp(FORTHCODE_SERVER_IP))
      : void 0;
    
    const clientTransformed = transformClientDataFromOffice(user);

    // Save other stuff to store
    id?dispatch(setClientId(id)):void 0;
    clientTransformed.ipList
      ? dispatch(setIpList(clientTransformed.ipList))
      : void 0;
    clientTransformed.senderNumbers
      ? dispatch(setSenderNumbers(clientTransformed.senderNumbers))
      : void 0;
    clientTransformed.selectedSenderNumber
      ? dispatch(
        setSelectedSenderNumber(clientTransformed.selectedSenderNumber)
      )
      : void 0;
    clientTransformed.callbackUrl
      ? dispatch(setCallbackUrl(clientTransformed.callbackUrl))
      : void 0;
    clientTransformed.callbackApiKey
      ? dispatch(setCallbackApiKey(clientTransformed.callbackApiKey))
      : void 0;
    clientTransformed.companyInfo
      ? dispatch(setCompanyInfo(clientTransformed.companyInfo))
      : void 0;
    clientTransformed.accountType
      ? dispatch(setAccountType(clientTransformed.accountType))
      : void 0;
  } catch (error) {
    console.error("authActions | setUserData | error: ", error);
  }
};
